import {
  MEDIA_PLAYER_TRANSITION_FLAG_CAMEL,
  MEDIA_PLAYER_TRANSITION_FLAG_KEY,
  MEDIA_PLAYER_TRANSITION_VIEWERS,
  isMediaPlayerTransitionEnabled,
  parseMediaPlayerTransition,
  readMediaPlayerTransitionFlag,
} from '@/shared/media-player-transition';
import type { FeatureFlagTypes } from '@/shared/types';

export type FlagEditorKind = 'string' | 'number' | 'json';

export type FlagValueOrigin = 'launchdarkly' | 'simonSays' | 'override';

/** Editor for the session value. Booleans stay on the checkbox. */
export function flagEditorKind(value: unknown): FlagEditorKind | null {
  if (typeof value === 'boolean' || value === undefined) return null;
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') return 'string';
  return 'json';
}

export function flagValueDraft(value: unknown, pretty = false): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, pretty ? 2 : undefined) ?? '';
  } catch {
    return '';
  }
}

export function parseFlagOverrideDraft(
  kind: FlagEditorKind,
  draft: string,
): { ok: true; value: FeatureFlagTypes } | { ok: false } {
  if (kind === 'string') return { ok: true, value: draft };
  if (kind === 'number') {
    const trimmed = draft.trim();
    if (trimmed === '') return { ok: false };
    const value = Number(trimmed);
    if (!Number.isFinite(value)) return { ok: false };
    return { ok: true, value };
  }
  try {
    return { ok: true, value: JSON.parse(draft) as FeatureFlagTypes };
  } catch {
    return { ok: false };
  }
}

export function isMediaPlayerTransitionFlagKey(key: string): boolean {
  return key === MEDIA_PLAYER_TRANSITION_FLAG_CAMEL || key === MEDIA_PLAYER_TRANSITION_FLAG_KEY;
}

function isPlainRecord(value: unknown): value is { [key: string]: FeatureFlagTypes } {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The media-player value the flag row and the checkbox rows both display.
 * A session override wins, whichever key it was stored under. Otherwise the
 * live flag (camel, then kebab) is the value.
 */
export function sessionMediaTransitionValue(
  overrides: FlagMap,
  liveFlags: { [key: string]: unknown } | null | undefined,
): FeatureFlagTypes | undefined {
  if (flagIsPresent(overrides, MEDIA_PLAYER_TRANSITION_FLAG_CAMEL)) {
    return overrides?.[MEDIA_PLAYER_TRANSITION_FLAG_CAMEL];
  }
  if (flagIsPresent(overrides, MEDIA_PLAYER_TRANSITION_FLAG_KEY)) {
    return overrides?.[MEDIA_PLAYER_TRANSITION_FLAG_KEY];
  }
  const live = readMediaPlayerTransitionFlag(liveFlags);
  return live === undefined ? undefined : live as FeatureFlagTypes;
}

function mediaTransitionRecord(value: unknown): { [key: string]: FeatureFlagTypes } | undefined {
  let current = value;
  if (typeof current === 'string') {
    try {
      current = JSON.parse(current);
    } catch {
      return undefined;
    }
  }
  return isPlainRecord(current) ? current : undefined;
}

/**
 * Display projection of the checkbox rows. ``enabled`` turns every viewer on
 * unless that viewer is set. This object is not the session override: writing
 * the inherited viewers would pin them, and toggling ``enabled`` would no
 * longer reach the live app.
 */
export function mediaTransitionEditorValue(
  value: unknown,
): { [key: string]: FeatureFlagTypes } | undefined {
  const record = mediaTransitionRecord(value);
  if (!record) return undefined;
  const next: { [key: string]: FeatureFlagTypes } = { ...record };
  next.enabled = parseMediaPlayerTransition(record).enabled === true;
  for (const viewer of MEDIA_PLAYER_TRANSITION_VIEWERS) {
    next[viewer] = isMediaPlayerTransitionEnabled(record, viewer);
  }
  return next;
}

/** True when two media-player values turn on the same checkboxes. */
export function mediaTransitionModelsEqual(left: unknown, right: unknown): boolean {
  const a = mediaTransitionEditorValue(left);
  const b = mediaTransitionEditorValue(right);
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return flagValuesEqual(a, b);
}

/**
 * Checkbox toggle applied to the compact media-player object. Unknown JSON
 * keys stay. Inherited viewers are not copied on, and an explicit viewer key
 * is left in place when ``enabled`` changes, so a holdback such as
 * ``pdf: false`` still wins after the master flips back.
 */
export function nextMediaTransitionOverride(
  previous: { [key: string]: FeatureFlagTypes },
  live: unknown,
  patch: { [key: string]: FeatureFlagTypes },
): { [key: string]: FeatureFlagTypes } {
  const existing = flagIsPresent(previous, MEDIA_PLAYER_TRANSITION_FLAG_CAMEL)
    ? previous[MEDIA_PLAYER_TRANSITION_FLAG_CAMEL]
    : flagIsPresent(previous, MEDIA_PLAYER_TRANSITION_FLAG_KEY)
      ? previous[MEDIA_PLAYER_TRANSITION_FLAG_KEY]
      : undefined;
  const base = { ...(mediaTransitionRecord(existing !== undefined ? existing : live) ?? {}) };
  return { ...base, ...patch };
}

/** Where the media-player value in this session came from. */
export function mediaTransitionOrigin(
  overrides: { [key: string]: unknown } | null | undefined,
  simonSaysActive: boolean,
): FlagValueOrigin {
  if (
    overrides
    && (Object.prototype.hasOwnProperty.call(overrides, MEDIA_PLAYER_TRANSITION_FLAG_CAMEL)
      || Object.prototype.hasOwnProperty.call(overrides, MEDIA_PLAYER_TRANSITION_FLAG_KEY))
  ) {
    return 'override';
  }
  return simonSaysActive ? 'simonSays' : 'launchdarkly';
}

type FlagMap = { [key: string]: FeatureFlagTypes } | null | undefined;

/** Every flag from either source, except the internal-tools gate itself. */
export function alignedFlagKeys(
  ldFlags: FlagMap,
  serviceFlags: FlagMap,
): string[] {
  const keys = new Set<string>();
  for (const key of Object.keys(ldFlags ?? {})) {
    if (key !== 'powerUserOrg') keys.add(key);
  }
  for (const key of Object.keys(serviceFlags ?? {})) {
    if (key !== 'powerUserOrg') keys.add(key);
  }
  return [...keys].sort((left, right) => left.localeCompare(right));
}

export function flagIsPresent(flags: FlagMap, key: string): boolean {
  return !!flags && Object.prototype.hasOwnProperty.call(flags, key);
}

/** True when this session has a stored override for the flag. */
export function flagHasStoredOverride(overrides: FlagMap, key: string): boolean {
  if (!overrides) return false;
  if (isMediaPlayerTransitionFlagKey(key)) {
    return flagIsPresent(overrides, MEDIA_PLAYER_TRANSITION_FLAG_CAMEL)
      || flagIsPresent(overrides, MEDIA_PLAYER_TRANSITION_FLAG_KEY);
  }
  return flagIsPresent(overrides, key);
}

/**
 * True when a saved session override for this non-boolean flag differs from
 * the active source. An override that still equals that source is not labeled
 * as overriding.
 */
export function nonBooleanOverrideDiffers(
  overrides: FlagMap,
  key: string,
  activeSourceValue: FeatureFlagTypes | undefined,
  activeSourcePresent: boolean,
): boolean {
  if (!overrides) return false;
  const storedKey = isMediaPlayerTransitionFlagKey(key)
    ? (flagIsPresent(overrides, MEDIA_PLAYER_TRANSITION_FLAG_CAMEL)
      ? MEDIA_PLAYER_TRANSITION_FLAG_CAMEL
      : flagIsPresent(overrides, MEDIA_PLAYER_TRANSITION_FLAG_KEY)
        ? MEDIA_PLAYER_TRANSITION_FLAG_KEY
        : null)
    : (flagIsPresent(overrides, key) ? key : null);
  if (storedKey == null) return false;
  if (!activeSourcePresent) return true;
  return !flagValuesEqual(overrides[storedKey], activeSourceValue);
}

/** A JSON string stands for its value. Any other string stays text. */
function jsonStructure(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return value;
  }
}

/**
 * Stable text for one array element so the element list can be sorted.
 * Object keys are written in sorted order. Strings stay quoted, so a value
 * cannot be confused with a neighboring key.
 */
function canonicalElement(value: unknown): string {
  const current = jsonStructure(value);
  if (typeof current === 'string' || typeof current === 'boolean' || current === null) {
    return JSON.stringify(current);
  }
  if (typeof current === 'number') {
    return JSON.stringify(Object.is(current, -0) ? 0 : current);
  }
  if (Array.isArray(current)) {
    return `[${current.map(canonicalElement).sort().join(',')}]`;
  }
  if (isPlainRecord(current)) {
    const keys = Object.keys(current).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalElement(current[key])}`).join(',')}}`;
  }
  return 'null';
}

function jsonValuesEqual(left: unknown, right: unknown): boolean {
  const a = jsonStructure(left);
  const b = jsonStructure(right);
  if (Object.is(a, b)) return true;
  if (typeof a === 'number' && typeof b === 'number') {
    return a === b || (Number.isNaN(a) && Number.isNaN(b));
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    if (a.length < 2) return a.length === 0 || jsonValuesEqual(a[0], b[0]);
    const aKeys = a.map(canonicalElement).sort();
    const bKeys = b.map(canonicalElement).sort();
    for (let index = 0; index < aKeys.length; index += 1) {
      if (aKeys[index] !== bKeys[index]) return false;
    }
    return true;
  }
  if (!isPlainRecord(a) || !isPlainRecord(b)) return false;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(b, key) || !jsonValuesEqual(a[key], b[key])) {
      return false;
    }
  }
  return true;
}

/**
 * Same JSON value, not the same text. Object keys and array items may be in
 * any order. A JSON string is compared as the value it parses to. Objects
 * are walked and stop at the first difference; array items are sorted once.
 */
export function flagValuesEqual(
  left: FeatureFlagTypes | undefined,
  right: FeatureFlagTypes | undefined,
): boolean {
  if (left === undefined || right === undefined) return false;
  return jsonValuesEqual(left, right);
}
